import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useConversationActions } from '@/modules/chat/hooks/useConversationActions';
import type { CardAction } from '@/shared/components/ui';
import type { AttendanceAction, AttendanceCardConversation } from '../types';
import type { ConversationCardStatusTag } from '../components/cards/types';

export interface UseConversationCardBehaviorParams {
  conversation: AttendanceCardConversation;
  /** Ações resolvidas na página (close/reopen). attend/transfer ficam aqui. */
  onAction?: (action: AttendanceAction, conversation: AttendanceCardConversation) => void;
}

export interface ConversationCardBehavior {
  /** Id do usuário logado — insumo do `buildConversationCardFacts`. */
  currentUserId?: string;
  statusTag: ConversationCardStatusTag | null;
  /** Já no formato do `CardActionsMenu` shared. */
  actions: CardAction[];
  onCardClick: () => void;
  transferDialog: {
    isOpen: boolean;
    close: () => void;
    submit: (targetType: 'user' | 'team', targetId: string, notes?: string) => void;
    isLoading: boolean;
  };
}

const ACTION_LABELS: Record<AttendanceAction, string> = {
  attend: 'Atender',
  transfer: 'Transferir',
  close: 'Fechar',
  reopen: 'Reabrir',
};

/**
 * Todo o comportamento do card de conversa do Kanban de atendimentos.
 *
 * Existe porque o card tem TRÊS aparências (Detalhes/Normal/Compacto) e uma única
 * lógica: assumir atendimento, transferir, fechar/reabrir e navegar. Sem este
 * hook, a mutação de "assumir" e o diálogo de transferência seriam escritos três
 * vezes — e divergiriam na primeira manutenção.
 *
 * As variantes NÃO chamam este hook: quem chama é o contêiner
 * `ConversationCardImproved`, uma vez por card.
 */
export const useConversationCardBehavior = ({
  conversation,
  onAction,
}: UseConversationCardBehaviorParams): ConversationCardBehavior => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state } = useAuth();
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);

  const startAttendanceMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await api.post(`/conversations/${conversationId}/start`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversation.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['attendance-management'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-kanban'] });
      toast.success('Atendimento iniciado!');
      navigate(`/chat/${conversation.id}`);
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || 'Erro ao iniciar atendimento');
    },
  });

  const { transferConversation, isTransferring } = useConversationActions({
    conversationId: conversation.id,
    onTransferSuccess: () => {
      setIsTransferDialogOpen(false);
      toast.success('Conversa transferida com sucesso!');
    },
  });

  // Tag de status → StatusPill shared (F63: cores via tokens, nunca hex).
  // Semântica F64: conversa ativa = "Em atendimento" (azul/info); fechado =
  // concluído (verde/success); aguardando = atenção (âmbar/warning).
  const statusTag = useMemo<ConversationCardStatusTag | null>(() => {
    if (conversation.agentId && conversation.agentStatus === 'active' && conversation.status !== 'closed') {
      return { label: 'IA', variant: 'info' };
    }
    switch (conversation.status) {
      case 'waiting':
        return { label: 'Aguardando', variant: 'warning' };
      case 'active':
        return { label: 'Em atendimento', variant: 'info' };
      case 'closed':
        return { label: 'Fechado', variant: 'success' };
      default:
        return null;
    }
  }, [conversation.agentId, conversation.agentStatus, conversation.status]);

  const handleAction = useCallback(
    (action: AttendanceAction) => {
      if (action === 'attend') {
        startAttendanceMutation.mutate(conversation.id);
        return;
      }
      if (action === 'transfer') {
        setIsTransferDialogOpen(true);
        return;
      }
      onAction?.(action, conversation);
    },
    [conversation, onAction, startAttendanceMutation],
  );

  const actions = useMemo<CardAction[]>(() => {
    const available: AttendanceAction[] =
      conversation.status === 'waiting'
        ? ['attend', 'transfer']
        : conversation.status === 'active'
          ? ['transfer', 'close']
          : conversation.status === 'closed'
            ? ['reopen']
            : [];
    return available.map((action) => ({
      label: ACTION_LABELS[action],
      onClick: () => handleAction(action),
    }));
  }, [conversation.status, handleAction]);

  const onCardClick = useCallback(() => {
    const basePath = conversation.source === 'email' ? '/email' : '/chat';
    navigate(`${basePath}/${conversation.id}`);
  }, [conversation.id, conversation.source, navigate]);

  const submitTransfer = useCallback(
    (targetType: 'user' | 'team', targetId: string, notes?: string) => {
      transferConversation({
        ...(targetType === 'user' ? { toUserId: targetId } : { toTeamId: targetId }),
        notes,
      });
    },
    [transferConversation],
  );

  return {
    currentUserId: state.user?.id,
    statusTag,
    actions,
    onCardClick,
    transferDialog: {
      isOpen: isTransferDialogOpen,
      close: () => setIsTransferDialogOpen(false),
      submit: submitTransfer,
      isLoading: isTransferring,
    },
  };
};
